/**
 * PsicoSalud — Contexto de autenticación.
 *
 * Mantiene el usuario activo y expone las operaciones de inicio y cierre
 * de sesión. Al montar, intenta recuperar la sesión desde el token
 * persistido para que una recarga de página no expulse al profesional
 * en medio de una consulta.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { authApi } from '@/api';
import { ApiError, tokenStore } from '@/api/client';
import type { User } from '@/types';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!tokenStore.access) {
        setIsLoading(false);
        return;
      }
      try {
        const profile = await authApi.me();
        if (!cancelled) setUser(profile);
      } catch (error) {
        // Solo un rechazo explícito del servidor deja el token sin valor. Un
        // corte de red o una recarga con la petición todavía en vuelo caen
        // también en este `catch`, y borrar el token ahí expulsaría de la
        // sesión a un profesional que solo apretó F5.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          tokenStore.clear();
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    await authApi.login(email, password);
    setUser(await authApi.me());
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout }),
    [user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }
  return context;
}
